import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Snackbar,
  Alert,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  IconButton,
  Card,
  Tooltip,
  Grid,
  Stack,
} from "@mui/material";
import httpRequest from "../utils/httpRequest";
import moment from "moment";
import { Payment, History, Edit, Delete } from "@mui/icons-material";
import TablePagination from "@mui/material/TablePagination";
import Supplier from "./Supplier";
import BulkSupplierLedgerForm from "./BulkSupplierLedgerForm";

const SuppliersLedger = () => {
  const [suppliers, setSuppliers] = useState([]);
  const [financialYears, setFinancialYears] = useState([]);
  const [supplierLedgers, setSupplierLedgers] = useState([]);
  const [overallData, setOverallData] = useState({
    overallTotalCredit: "",
    overallTotalBalance: "",
  });
  const [pagination, setPagination] = useState({
    totalRecords: 0,
    currentPage: 1,
    totalPages: 0,
    pageSize: 10,
  });
  const [firms, setFirms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    supplier_id: "",
    financial_year_id: "",
    firm_id: "",
    bill_type: "",
  });

  const [openDialog, setOpenDialog] = useState(false);
  const [openBulkDialog, setOpenBulkDialog] = useState(false);
  const [openHistoryDialog, setOpenHistoryDialog] = useState(false);
  const [openPaymentDialog, setOpenPaymentDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [ledgerToDelete, setLedgerToDelete] = useState(null);
  const [openDeletePaymentDialog, setOpenDeletePaymentDialog] = useState(false);
  const [paymentToDelete, setPaymentToDelete] = useState(null);
  const [newLedger, setNewLedger] = useState({
    supplier_id: "",
    financial_year_id: "",
    sub_supplier_name: "",
    bill_date: "",
    bill_number: "",
    bill_type: "",
    credit_amount: "",
    description: "",
    firm_id: "",
  });

  const [editLedger, setEditLedger] = useState({
    id: "",
    supplier_id: "",
    financial_year_id: "",
    sub_supplier_name: "",
    bill_date: "",
    bill_number: "",
    bill_type: "",
    credit_amount: "",
    description: "",
    firm_id: "",
  });

  const [newPayment, setNewPayment] = useState({
    ledger_id: "",
    payment_mode: "",
    debit_amount: "",
    transaction_id: "",
    description: "",
    transaction_date: new Date().toISOString().split('T')[0],
  });

  const [history, setHistory] = useState([]);
  const [currentLedger, setCurrentLedger] = useState(null);
  const [shouldReopenHistory, setShouldReopenHistory] = useState(false);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const fetchData = async () => {
    setLoading(true);
    try {
      const suppliersData = await httpRequest("suppliers/getAll", "GET");
      const financialYearsData = await httpRequest(
        "invoice/getAllFinancialYears",
        "GET"
      );
      const firmsData = await httpRequest("firm/getAll", "GET");

      const currentFinancialYearId = getCurrentFinancialYearId(financialYearsData);
      setNewLedger((prev) => {
        if (!prev.financial_year_id) {
          return { ...prev, financial_year_id: currentFinancialYearId };
        }
        return prev;
      });
      setFilters((prev) => ({
        ...prev,
        financial_year_id: currentFinancialYearId,
      }));
      setSuppliers(suppliersData);
      setFinancialYears(financialYearsData);
      setFirms(firmsData);
    } catch (err) {
      setError("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  }, [filters]);

  useEffect(() => {
    fetchSupplierLedgers(pagination.currentPage, pagination.pageSize);
  }, [pagination.currentPage, pagination.pageSize, filters]);

  const fetchSupplierLedgers = async (page, pageSize) => {
    setLoading(true);
    try {
      const response = await httpRequest(
        "suppliers/ledgers",
        "GET",
        {},
        { params: { ...filters }, page, pageSize }
      );
      setSupplierLedgers(response.supplierLedgers);
      setOverallData({
        overallTotalCredit: response.overallTotalCredit,
        overallTotalBalance: response.overallTotalBalance,
      });
      setPagination((prev) => ({
        ...prev,
        totalRecords: response.pagination.totalRecords,
        totalPages: response.pagination.totalPages,
      }));
    } catch (error) {
      console.error("Error fetching supplier ledgers:", error);
      setError("Failed to fetch supplier ledgers");
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (event, newPage) => {
    setPagination((prev) => ({ ...prev, currentPage: newPage + 1 }));
  };

  const handlePageSizeChange = (event) => {
    setPagination((prev) => ({
      ...prev,
      pageSize: parseInt(event.target.value, 10),
      currentPage: 1,
    }));
  };

  const handleOpenDialog = () => {
    setOpenDialog(true);
  };

  const handleOpenBulkDialog = () => {
    setOpenBulkDialog(true);
  };

  const handleFilterChange = (e) => {
    setFilters({
      ...filters,
      [e.target.name]: e.target.value,
    });
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleOpenPaymentDialog = (ledger) => {
    setNewPayment({
      ...newPayment,
      ledger_id: ledger.id,
    });
    setOpenPaymentDialog(true);
    setShouldReopenHistory(true);
    if (openHistoryDialog) {
      setOpenHistoryDialog(false);
    }
  };

  const handleOpenHistoryDialog = (ledger) => {
    setCurrentLedger(ledger);
    setOpenHistoryDialog(true);
    handleViewHistory(ledger);
  };

  const handleOpenEditDialog = (ledger) => {
    setEditLedger({
      id: ledger.id,
      supplier_id: ledger.supplier_id,
      financial_year_id: ledger.financial_year_id,
      sub_supplier_name: ledger.sub_supplier_name || "",
      bill_date: moment(ledger.bill_date).format("YYYY-MM-DD"),
      bill_number: ledger.bill_number || "",
      bill_type: ledger.bill_type || "",
      credit_amount: ledger.credit_amount,
      description: ledger.description || "",
      firm_id: ledger.firm_id,
    });
    setOpenEditDialog(true);
  };

  const getFinancialYearIdByDate = (dateString) => {
    if (!dateString || !financialYears.length) return "";
    
    const date = new Date(dateString);
    for (const year of financialYears) {
      const startDate = new Date(year.start_date);
      const endDate = new Date(year.end_date);
      if (date >= startDate && date <= endDate) {
        return year.id;
      }
    }
    return financialYears.length > 0 ? financialYears[0].id : "";
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditLedger(prev => {
      const updated = {
        ...prev,
        [name]: value,
      };
      
      // Auto-calculate financial year when bill date changes
      if (name === 'bill_date') {
        updated.financial_year_id = getFinancialYearIdByDate(value);
      }
      
      return updated;
    });
  };

  const handleEditSubmit = async () => {
    try {
      await httpRequest(`suppliers/ledger/${editLedger.id}`, "PUT", {
        supplier_id: editLedger.supplier_id,
        financial_year_id: editLedger.financial_year_id,
        sub_supplier_name: editLedger.sub_supplier_name,
        bill_date: editLedger.bill_date,
        bill_number: editLedger.bill_number,
        bill_type: editLedger.bill_type,
        credit_amount: editLedger.credit_amount,
        description: editLedger.description,
        firm_id: editLedger.firm_id,
      });
      setSnackbar({
        open: true,
        message: "Supplier ledger updated successfully!",
        severity: "success",
      });
      fetchSupplierLedgers(pagination.currentPage, pagination.pageSize);
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.response?.data?.error || "Failed to update supplier ledger",
        severity: "error",
      });
    } finally {
      setOpenEditDialog(false);
      setEditLedger({
        id: "",
        supplier_id: "",
        financial_year_id: "",
        sub_supplier_name: "",
        bill_date: "",
        bill_number: "",
        bill_type: "",
        credit_amount: "",
        description: "",
        firm_id: "",
      });
    }
  };

  const handleCloseEditDialog = () => {
    setOpenEditDialog(false);
    setEditLedger({
      id: "",
      supplier_id: "",
      financial_year_id: "",
      sub_supplier_name: "",
      bill_date: "",
      bill_number: "",
      bill_type: "",
      credit_amount: "",
      description: "",
      firm_id: "",
    });
  };

  const handleOpenDeleteDialog = (ledger) => {
    setLedgerToDelete(ledger);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setLedgerToDelete(null);
  };

  const handleDeleteLedger = async () => {
    if (!ledgerToDelete) return;

    try {
      await httpRequest(`suppliers/ledger/${ledgerToDelete.id}`, "DELETE");
      setSnackbar({
        open: true,
        message: "Supplier ledger deleted successfully!",
        severity: "success",
      });
      fetchSupplierLedgers(pagination.currentPage, pagination.pageSize);
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.response?.data?.error || "Failed to delete supplier ledger",
        severity: "error",
      });
    } finally {
      handleCloseDeleteDialog();
    }
  };

  const handleOpenDeletePaymentDialog = (payment) => {
    setPaymentToDelete(payment);
    setOpenDeletePaymentDialog(true);
  };

  const handleCloseDeletePaymentDialog = () => {
    setOpenDeletePaymentDialog(false);
    setPaymentToDelete(null);
  };

  const handleDeletePayment = async () => {
    if (!paymentToDelete) return;

    try {
      await httpRequest(`suppliers/transaction/${paymentToDelete.id}`, "DELETE");
      setSnackbar({
        open: true,
        message: "Payment transaction deleted successfully!",
        severity: "success",
      });
      // Refresh the history data
      if (currentLedger) {
        handleViewHistory(currentLedger);
      }
      // Refresh the main ledger list
      fetchSupplierLedgers(pagination.currentPage, pagination.pageSize);
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.response?.data?.error || "Failed to delete payment transaction",
        severity: "error",
      });
    } finally {
      handleCloseDeletePaymentDialog();
    }
  };

  const getCurrentFinancialYearId = (financialYears) => {
    const currentDate = new Date()
    for (const year of financialYears) {
      const startDate = new Date(year.start_date);
      const endDate = new Date(year.end_date);
      if (currentDate >= startDate && currentDate <= endDate) {
        return year.id;
      }
    }
    return "";
  };

  const handlePaymentCloseDialog = () => {
    setOpenPaymentDialog(false);
    setNewPayment({
      ledger_id: "",
      debit_amount: "",
      transaction_id: "",
      payment_mode: "",
      description: "",
      transaction_date: new Date().toISOString().split('T')[0],
    });
    
    if (currentLedger && shouldReopenHistory) {
      setTimeout(() => {
        console.log('Reopening history dialog after payment cancellation');
        setOpenHistoryDialog(true);
        handleViewHistory(currentLedger);
      }, 100);
    }
  };

  const handleHistoryCloseDialog = () => {
    setOpenHistoryDialog(false);
    setCurrentLedger(null);
    setShouldReopenHistory(false);
  };

  const handleChange = (e) => {
    setNewLedger({
      ...newLedger,
      [e.target.name]: e.target.value,
    });
  };

  const handlePaymentChange = (e) => {
    setNewPayment({
      ...newPayment,
      [e.target.name]: e.target.value,
    });
  };

  const handleMakePayment = async () => {
    try {
      await httpRequest("suppliers/makePayment", "POST", {
        supplier_ledger_id: newPayment.ledger_id,
        debit_amount: newPayment.debit_amount,
        transaction_id: newPayment.transaction_id,
        payment_mode: newPayment.payment_mode,
        description: newPayment.description,
        transaction_date: newPayment.transaction_date,
      });
      setSnackbar({
        open: true,
        message: "Payment successful!",
        severity: "success",
      });
      fetchSupplierLedgers(pagination.currentPage, pagination.pageSize);
      
      setOpenPaymentDialog(false);
      setNewPayment({
        ledger_id: "",
        debit_amount: "",
        transaction_id: "",
        payment_mode: "",
        description: "",
        transaction_date: new Date().toISOString().split('T')[0], // Reset to current date
      });
      
      if (currentLedger && shouldReopenHistory) {
        setTimeout(() => {
          console.log('Reopening history dialog for ledger:', currentLedger.id);
          setOpenHistoryDialog(true);
          handleViewHistory(currentLedger);
          setShouldReopenHistory(false);
        }, 100);
      } else {
        console.log('No currentLedger found or shouldReopenHistory is false');
      }
    } catch (err) {
      setSnackbar({ open: true, message: "Payment failed", severity: "error" });
    }
  };

  const handleViewHistory = async (ledger) => {
    try {
      const historyData = await httpRequest(
        `suppliers/history/${ledger.id}`,
        "GET"
      );
      setHistory(historyData);
    } catch (err) {
      setSnackbar({
        open: true,
        message: "Failed to fetch history",
        severity: "error",
      });
    }
  };

  const handleSubmit = async () => {
    try {
      await httpRequest("suppliers/addEntryToLedger", "POST", newLedger);
      setSnackbar({
        open: true,
        message: "Supplier ledger added successfully!",
        severity: "success",
      });
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.response?.data?.error || "Failed to add supplier ledger",
        severity: "error",
      });
    } finally {
      setOpenDialog(false);
      setNewLedger({
        supplier_id: "",
        financial_year_id: "",
        bill_date: "",
        bill_number: "",
        bill_type: "",
        credit_amount: "",
        description: "",
      });
      fetchSupplierLedgers(pagination.currentPage, pagination.pageSize);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", marginTop: 2 }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Typography variant="h4">Supplier Ledgers Management</Typography>

        <Stack direction="row" spacing={2}>
          <Supplier setSuppliers={setSuppliers} />
          <Button variant="outlined" onClick={handleOpenBulkDialog}>
            Add Entries
          </Button>
        </Stack>
      </Box>
      <Box mb={2}>
        <Card variant="outlined" sx={{ borderRadius: 2, padding: 2 }}>
          <Grid container spacing={2}>
            <Grid size={4}>
              <Typography variant="body2" color="text.secondary">
                Total Credit
              </Typography>
              <Typography variant="h6" color="green">
                ₹{overallData.overallTotalCredit}
              </Typography>
            </Grid>
            <Grid size={4}>
              <Typography variant="body2" color="danger">
                Total Balance
              </Typography>
              <Typography
                variant="h6"
                fontWeight={600}
                color={
                  overallData.overallTotalBalance > 0 ? "#D32F2F" : "#1976D2"
                }
              >
                ₹{overallData.overallTotalBalance}
              </Typography>
            </Grid>
          </Grid>
        </Card>
      </Box>
      <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
        <FormControl fullWidth>
          <InputLabel>Supplier</InputLabel>
          <Select
            name="supplier_id"
            value={filters.supplier_id}
            onChange={handleFilterChange}
            label="Supplier"
          >
            <MenuItem value="">All</MenuItem>
            {suppliers.map((supplier) => (
              <MenuItem key={supplier.id} value={supplier.id}>
                {supplier.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth>
          <InputLabel>Financial Year</InputLabel>
          <Select
            name="financial_year_id"
            value={filters.financial_year_id}
            onChange={handleFilterChange}
            label="Financial Year"
          >
            <MenuItem value="">All</MenuItem>
            {financialYears.map((year) => (
              <MenuItem key={year.id} value={year.id}>
                {year.description}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth>
          <InputLabel>Firm</InputLabel>
          <Select
            name="firm_id"
            value={filters.firm_id}
            onChange={handleFilterChange}
            label="Firm"
          >
            <MenuItem value="">All</MenuItem>
            {firms.map((firm) => (
              <MenuItem key={firm.id} value={firm.id}>
                {firm.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth>
          <InputLabel>Bill Type</InputLabel>
          <Select
            name="bill_type"
            value={filters.bill_type}
            onChange={handleFilterChange}
            label="Bill Type"
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="kachcha">Kachcha</MenuItem>
            <MenuItem value="gst">GST</MenuItem>
          </Select>
        </FormControl>
      </Box>
      <Dialog open={openDialog} onClose={handleCloseDialog}>
        <DialogTitle>Add Supplier Ledger</DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="normal">
            <InputLabel>Supplier</InputLabel>
            <Select
              name="supplier_id"
              value={newLedger.supplier_id}
              onChange={handleChange}
              label="Supplier"
            >
              {suppliers.map((supplier) => (
                <MenuItem key={supplier.id} value={supplier.id}>
                  {supplier.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="Sub-supplier Name"
            fullWidth
            margin="normal"
            name="sub_supplier_name"
            value={newLedger.sub_supplier_name}
            onChange={handleChange}
          />

          <FormControl fullWidth margin="normal">
            <InputLabel>Financial Year</InputLabel>
            <Select
              name="financial_year_id"
              value={newLedger.financial_year_id}
              onChange={handleChange}
              label="Financial Year"
            >
              {financialYears.map((year) => (
                <MenuItem key={year.description} value={year.id}>
                  {year.description}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth margin="normal">
            <InputLabel>In name of (Firm)</InputLabel>
            <Select
              name="firm_id"
              value={newLedger.firm_id}
              onChange={handleChange}
              label="In name of (Firm)"
            >
              {firms.map((firm) => (
                <MenuItem key={firm.id} value={firm.id}>
                  {firm.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="Bill Date"
            type="date"
            fullWidth
            margin="normal"
            name="bill_date"
            value={newLedger.bill_date}
            onChange={handleChange}
            InputLabelProps={{
              shrink: true,
            }}
          />

          <TextField
            label="Bill Number"
            fullWidth
            margin="normal"
            name="bill_number"
            value={newLedger.bill_number}
            onChange={handleChange}
          />

          <FormControl fullWidth margin="normal">
            <InputLabel>Bill Type</InputLabel>
            <Select
              name="bill_type"
              value={newLedger.bill_type}
              onChange={handleChange}
              label="Bill Type"
            >
              <MenuItem key="kachcha" value="kachcha">
                kachcha
              </MenuItem>
              <MenuItem key="gst" value="gst">
                GST
              </MenuItem>
            </Select>
          </FormControl>

          <TextField
            label="Credit Amount"
            type="number"
            fullWidth
            margin="normal"
            name="credit_amount"
            value={newLedger.credit_amount}
            onChange={handleChange}
          />
          <TextField
            label="Description"
            fullWidth
            margin="normal"
            name="description"
            value={newLedger.description}
            onChange={handleChange}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="primary">
            Cancel
          </Button>
          <Button onClick={handleSubmit} color="primary">
            Add Ledger
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog 
        open={openPaymentDialog} 
        onClose={handlePaymentCloseDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          }
        }}
      >
        <DialogTitle 
          sx={{ 
            backgroundColor: 'primary.main',
            color: 'white',
            textAlign: 'center',
            py: 3,
            '& .MuiTypography-root': {
              fontSize: '1.5rem',
              fontWeight: 600,
            }
          }}
        >
          Add Payment Transaction
        </DialogTitle>
        <DialogContent sx={{ p: 4 }}>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12 }} sx={{mt: 2}}>
              <TextField
                label="Debit Amount"
                type="number"
                fullWidth
                name="debit_amount"
                value={newPayment.debit_amount}
                onChange={handlePaymentChange}
                inputProps={{ min: 0, step: "0.01" }}
                error={
                  !!isNaN(newPayment.debit_amount) || newPayment.debit_amount < 0
                }
                helperText={
                  isNaN(newPayment.debit_amount)
                    ? "Please enter a valid number"
                    : newPayment.debit_amount < 0
                    ? "Amount cannot be negative"
                    : ""
                }
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '&:hover fieldset': {
                      borderColor: 'primary.main',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: 'primary.main',
                    },
                  },
                }}
              />
            </Grid>
            
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Transaction Date"
                type="date"
                fullWidth
                name="transaction_date"
                value={newPayment.transaction_date}
                onChange={handlePaymentChange}
                InputLabelProps={{
                  shrink: true,
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '&:hover fieldset': {
                      borderColor: 'primary.main',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: 'primary.main',
                    },
                  },
                }}
              />
            </Grid>
            
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Payment Mode"
                select
                fullWidth
                name="payment_mode"
                value={newPayment.payment_mode}
                onChange={handlePaymentChange}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '&:hover fieldset': {
                      borderColor: 'primary.main',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: 'primary.main',
                    },
                  },
                }}
              >
                <MenuItem value="Cash">Cash</MenuItem>
                <MenuItem value="UPI">UPI</MenuItem>
                <MenuItem value="Bank Transfer">Bank Transfer</MenuItem>
                <MenuItem value="Credit Card">Credit Card</MenuItem>
                <MenuItem value="Debit Card">Debit Card</MenuItem>
                <MenuItem value="Cheque">Cheque</MenuItem>
                <MenuItem value="Paytm">Paytm</MenuItem>
                <MenuItem value="PhonePe">PhonePe</MenuItem>
                <MenuItem value="Google Pay">Google Pay</MenuItem>
                <MenuItem value="Other">Other</MenuItem>
              </TextField>
            </Grid>
            
            <Grid size={{ xs: 12 }}>
              <TextField
                label="Transaction ID"
                type="text"
                fullWidth
                name="transaction_id"
                value={newPayment.transaction_id}
                onChange={handlePaymentChange}
                placeholder="Enter transaction ID or reference number"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '&:hover fieldset': {
                      borderColor: 'primary.main',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: 'primary.main',
                    },
                  },
                }}
              />
            </Grid>
            
            <Grid size={{ xs: 12 }}>
              <TextField
                label="Description"
                fullWidth
                name="description"
                value={newPayment.description}
                onChange={handlePaymentChange}
                multiline
                rows={3}
                placeholder="Enter payment description or notes"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '&:hover fieldset': {
                      borderColor: 'primary.main',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: 'primary.main',
                    },
                  },
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button 
            onClick={handlePaymentCloseDialog} 
            variant="outlined"
            sx={{ 
              borderRadius: 2,
              px: 3,
              py: 1,
              borderColor: 'primary.main',
              color: 'primary.main',
              '&:hover': {
                borderColor: 'primary.dark',
                backgroundColor: 'primary.50',
              }
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleMakePayment} 
            variant="contained"
            sx={{ 
              borderRadius: 2,
              px: 3,
              py: 1,
              backgroundColor: 'primary.main',
              '&:hover': {
                backgroundColor: 'primary.dark',
              }
            }}
          >
            Add Transaction
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={openHistoryDialog}
        onClose={handleHistoryCloseDialog}
        fullWidth
        maxWidth="lg"
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          }
        }}
      >
        <DialogTitle 
          sx={{ 
            backgroundColor: 'primary.main',
            color: 'white',
            textAlign: 'center',
            py: 3,
            '& .MuiTypography-root': {
              fontSize: '1.5rem',
              fontWeight: 600,
            }
          }}
        >
          Ledger Transaction History
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <TableContainer 
            component={Paper} 
            sx={{ 
              boxShadow: 'none',
              '& .MuiTable-root': {
                borderCollapse: 'separate',
                borderSpacing: 0,
              }
            }}
          >
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: 'grey.50' }}>
                  <TableCell 
                    sx={{ 
                      fontWeight: 600, 
                      color: 'primary.main',
                      borderBottom: '2px solid',
                      borderBottomColor: 'primary.main',
                      py: 2
                    }}
                  >
                    ID
                  </TableCell>
                  <TableCell 
                    sx={{ 
                      fontWeight: 600, 
                      color: 'primary.main',
                      borderBottom: '2px solid',
                      borderBottomColor: 'primary.main',
                      py: 2
                    }}
                  >
                    Credit Amount
                  </TableCell>
                  <TableCell 
                    sx={{ 
                      fontWeight: 600, 
                      color: 'primary.main',
                      borderBottom: '2px solid',
                      borderBottomColor: 'primary.main',
                      py: 2
                    }}
                  >
                    Debit Amount
                  </TableCell>
                  <TableCell 
                    sx={{ 
                      fontWeight: 600, 
                      color: 'primary.main',
                      borderBottom: '2px solid',
                      borderBottomColor: 'primary.main',
                      py: 2
                    }}
                  >
                    Description
                  </TableCell>
                  <TableCell 
                    sx={{ 
                      fontWeight: 600, 
                      color: 'primary.main',
                      borderBottom: '2px solid',
                      borderBottomColor: 'primary.main',
                      py: 2
                    }}
                  >
                    Transaction ID
                  </TableCell>
                  <TableCell 
                    sx={{ 
                      fontWeight: 600, 
                      color: 'primary.main',
                      borderBottom: '2px solid',
                      borderBottomColor: 'primary.main',
                      py: 2
                    }}
                  >
                    Payment Mode
                  </TableCell>
                  <TableCell 
                    sx={{ 
                      fontWeight: 600, 
                      color: 'primary.main',
                      borderBottom: '2px solid',
                      borderBottomColor: 'primary.main',
                      py: 2
                    }}
                  >
                    Transaction Date
                  </TableCell>
                  <TableCell 
                    sx={{ 
                      fontWeight: 600, 
                      color: 'primary.main',
                      borderBottom: '2px solid',
                      borderBottomColor: 'primary.main',
                      py: 2
                    }}
                  >
                    Running Balance
                  </TableCell>
                  <TableCell 
                    sx={{ 
                      fontWeight: 600, 
                      color: 'primary.main',
                      borderBottom: '2px solid',
                      borderBottomColor: 'primary.main',
                      py: 2
                    }}
                  >
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {history.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                      <Typography variant="body1" color="text.secondary">
                        No transaction history found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  history.map((data, index) => (
                    <TableRow 
                      key={data.id}
                      sx={{ 
                        '&:hover': {
                          backgroundColor: 'grey.50',
                        },
                        '&:nth-of-type(even)': {
                          backgroundColor: 'grey.25',
                        }
                      }}
                    >
                      <TableCell sx={{ py: 2, fontWeight: 500 }}>
                        #{data.id}
                      </TableCell>
                      <TableCell 
                        sx={{ 
                          py: 2,
                          color: data.credit_amount > 0 ? 'success.main' : 'text.secondary',
                          fontWeight: data.credit_amount > 0 ? 600 : 400
                        }}
                      >
                        {data.credit_amount > 0 ? `₹${data.credit_amount}` : '-'}
                      </TableCell>
                      <TableCell 
                        sx={{ 
                          py: 2,
                          color: data.debit_amount > 0 ? 'error.main' : 'text.secondary',
                          fontWeight: data.debit_amount > 0 ? 600 : 400
                        }}
                      >
                        {data.debit_amount > 0 ? `₹${data.debit_amount}` : '-'}
                      </TableCell>
                      <TableCell sx={{ py: 2, maxWidth: 200 }}>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {data.description || "N/A"}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                          {data.transaction_id || "N/A"}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 2 }}>
                        {data.payment_mode ? (
                          <Box
                            sx={{
                              display: 'inline-block',
                              px: 1.5,
                              py: 0.5,
                              borderRadius: 1,
                              backgroundColor: 'primary.50',
                              color: 'primary.main',
                              fontSize: '0.75rem',
                              fontWeight: 500,
                            }}
                          >
                            {data.payment_mode}
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            N/A
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ py: 2 }}>
                        <Typography variant="body2">
                          {new Date(data.createdAt).toLocaleDateString('en-IN', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric'
                          })}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {new Date(data.createdAt).toLocaleTimeString('en-IN', {
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </Typography>
                      </TableCell>
                      <TableCell 
                        sx={{ 
                          py: 2,
                          fontWeight: 600,
                          color: data.balance > 0 ? 'error.main' : 'success.main'
                        }}
                      >
                        ₹{data.balance || 0}
                      </TableCell>
                      <TableCell sx={{ py: 2 }}>
                        {data.debit_amount > 0 && (
                          <Tooltip title="Delete Payment" placement="top">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleOpenDeletePaymentDialog(data)}
                              sx={{ 
                                '&:hover': {
                                  backgroundColor: 'error.50',
                                }
                              }}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 2 }}>
          <Button 
            onClick={() => handleOpenPaymentDialog(currentLedger)}
            variant="contained"
            color="success"
            startIcon={<Payment />}
            sx={{ 
              borderRadius: 2,
              px: 3,
              py: 1,
              backgroundColor: 'success.main',
              '&:hover': {
                backgroundColor: 'success.dark',
              }
            }}
          >
            Make Payment
          </Button>
          <Button 
            onClick={handleHistoryCloseDialog} 
            variant="contained"
            sx={{ 
              borderRadius: 2,
              px: 3,
              py: 1,
              backgroundColor: 'primary.main',
              '&:hover': {
                backgroundColor: 'primary.dark',
              }
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
      
      <Dialog 
        open={openEditDialog} 
        onClose={handleCloseEditDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          }
        }}
      >
        <DialogTitle 
          sx={{ 
            backgroundColor: 'primary.main',
            color: 'white',
            textAlign: 'center',
            py: 3,
            '& .MuiTypography-root': {
              fontSize: '1.5rem',
              fontWeight: 600,
            }
          }}
        >
          Edit Supplier Ledger Entry
        </DialogTitle>
        <DialogContent sx={{ p: 4 }}>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth margin="normal">
                <InputLabel>Supplier</InputLabel>
                <Select
                  name="supplier_id"
                  value={editLedger.supplier_id}
                  onChange={handleEditChange}
                  label="Supplier"
                >
                  {suppliers.map((supplier) => (
                    <MenuItem key={supplier.id} value={supplier.id}>
                      {supplier.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Sub-supplier Name"
                fullWidth
                margin="normal"
                name="sub_supplier_name"
                value={editLedger.sub_supplier_name}
                onChange={handleEditChange}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Financial Year"
                fullWidth
                margin="normal"
                name="financial_year_display"
                value={financialYears.find(year => year.id === editLedger.financial_year_id)?.description || "Auto-calculated"}
                InputProps={{
                  readOnly: true,
                }}
                helperText="Automatically calculated based on bill date"
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth margin="normal">
                <InputLabel>In name of (Firm)</InputLabel>
                <Select
                  name="firm_id"
                  value={editLedger.firm_id}
                  onChange={handleEditChange}
                  label="In name of (Firm)"
                >
                  {firms.map((firm) => (
                    <MenuItem key={firm.id} value={firm.id}>
                      {firm.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Bill Date"
                type="date"
                fullWidth
                margin="normal"
                name="bill_date"
                value={editLedger.bill_date}
                onChange={handleEditChange}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Bill Number"
                fullWidth
                margin="normal"
                name="bill_number"
                value={editLedger.bill_number}
                onChange={handleEditChange}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth margin="normal">
                <InputLabel>Bill Type</InputLabel>
                <Select
                  name="bill_type"
                  value={editLedger.bill_type}
                  onChange={handleEditChange}
                  label="Bill Type"
                >
                  <MenuItem value="kachcha">Kachcha</MenuItem>
                  <MenuItem value="gst">GST</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Credit Amount"
                type="number"
                fullWidth
                margin="normal"
                name="credit_amount"
                value={editLedger.credit_amount}
                onChange={handleEditChange}
                inputProps={{ min: 0, step: "0.01" }}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <TextField
                label="Description"
                fullWidth
                margin="normal"
                name="description"
                value={editLedger.description}
                onChange={handleEditChange}
                multiline
                rows={3}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button 
            onClick={handleCloseEditDialog} 
            variant="outlined"
            sx={{ 
              borderRadius: 2,
              px: 3,
              py: 1,
              borderColor: 'primary.main',
              color: 'primary.main',
              '&:hover': {
                borderColor: 'primary.dark',
                backgroundColor: 'primary.50',
              }
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleEditSubmit} 
            variant="contained"
            sx={{ 
              borderRadius: 2,
              px: 3,
              py: 1,
              backgroundColor: 'primary.main',
              '&:hover': {
                backgroundColor: 'primary.dark',
              }
            }}
          >
            Update Ledger
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Delete Confirmation Dialog */}
      <Dialog 
        open={openDeleteDialog} 
        onClose={handleCloseDeleteDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          }
        }}
      >
        <DialogTitle 
          sx={{ 
            backgroundColor: 'error.main',
            color: 'white',
            textAlign: 'center',
            py: 3,
            '& .MuiTypography-root': {
              fontSize: '1.5rem',
              fontWeight: 600,
            }
          }}
        >
          Delete Ledger Entry
        </DialogTitle>
        <DialogContent sx={{ p: 4 }}>
          <Typography variant="body1" sx={{ mb: 2, mt: 2 }}>
            Are you sure you want to delete this ledger entry?
          </Typography>
          {ledgerToDelete && (
            <Box sx={{ 
              backgroundColor: 'grey.50', 
              p: 2, 
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'grey.300'
            }}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                Entry Details:
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Supplier:</strong> {ledgerToDelete.Supplier?.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Bill Number:</strong> {ledgerToDelete.bill_number || 'N/A'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Bill Date:</strong> {moment(ledgerToDelete.bill_date).format("DD-MM-YYYY")}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Credit Amount:</strong> ₹{ledgerToDelete.credit_amount}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Description:</strong> {ledgerToDelete.description || 'N/A'}
              </Typography>
            </Box>
          )}
          <Typography variant="body2" color="error" sx={{ mt: 2, fontWeight: 600 }}>
            ⚠️ This action cannot be undone. All related transaction records will also be deleted.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button 
            onClick={handleCloseDeleteDialog} 
            variant="outlined"
            sx={{ 
              borderRadius: 2,
              px: 3,
              py: 1,
              borderColor: 'grey.400',
              color: 'grey.700',
              '&:hover': {
                borderColor: 'grey.600',
                backgroundColor: 'grey.50',
              }
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteLedger} 
            variant="contained"
            color="error"
            sx={{ 
              borderRadius: 2,
              px: 3,
              py: 1,
              backgroundColor: 'error.main',
              '&:hover': {
                backgroundColor: 'error.dark',
              }
            }}
          >
            Delete Entry
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Delete Payment Confirmation Dialog */}
      <Dialog 
        open={openDeletePaymentDialog} 
        onClose={handleCloseDeletePaymentDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          }
        }}
      >
        <DialogTitle 
          sx={{ 
            backgroundColor: 'error.main',
            color: 'white',
            textAlign: 'center',
            py: 3,
            '& .MuiTypography-root': {
              fontSize: '1.5rem',
              fontWeight: 600,
            }
          }}
        >
          Delete Payment Transaction
        </DialogTitle>
        <DialogContent sx={{ p: 4 }}>
          <Typography variant="body1" sx={{ mb: 2, mt: 2 }}>
            Are you sure you want to delete this payment transaction?
          </Typography>
          {paymentToDelete && (
            <Box sx={{ 
              backgroundColor: 'grey.50', 
              p: 2, 
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'grey.300'
            }}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                Payment Details:
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Amount:</strong> ₹{paymentToDelete.debit_amount}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Payment Mode:</strong> {paymentToDelete.payment_mode || 'N/A'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Transaction ID:</strong> {paymentToDelete.transaction_id || 'N/A'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Date:</strong> {new Date(paymentToDelete.createdAt).toLocaleDateString('en-IN')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Description:</strong> {paymentToDelete.description || 'N/A'}
              </Typography>
            </Box>
          )}
          <Typography variant="body2" color="error" sx={{ mt: 2, fontWeight: 600 }}>
            ⚠️ This action cannot be undone. The payment will be permanently removed from the ledger.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button 
            onClick={handleCloseDeletePaymentDialog} 
            variant="outlined"
            sx={{ 
              borderRadius: 2,
              px: 3,
              py: 1,
              borderColor: 'grey.400',
              color: 'grey.700',
              '&:hover': {
                borderColor: 'grey.600',
                backgroundColor: 'grey.50',
              }
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDeletePayment} 
            variant="contained"
            color="error"
            sx={{ 
              borderRadius: 2,
              px: 3,
              py: 1,
              backgroundColor: 'error.main',
              '&:hover': {
                backgroundColor: 'error.dark',
              }
            }}
          >
            Delete Payment
          </Button>
        </DialogActions>
      </Dialog>
      
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
      <TableContainer component={Paper} sx={{ mt: 2 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <b>Supplier</b>
              </TableCell>
              <TableCell>
                <b>Sub-Supplier</b>
              </TableCell>
              <TableCell>
                <b>Account Number</b>
              </TableCell>
              <TableCell>
                <b>Financial Year</b>
              </TableCell>
              <TableCell>
                <b>In the name of (firm)</b>
              </TableCell>
              <TableCell>
                <b>Bill Date</b>
              </TableCell>
              <TableCell>
                <b>Bill Number</b>
              </TableCell>
              <TableCell>
                <b>Bill Type</b>
              </TableCell>
              <TableCell>
                <b>Credit Amount</b>
              </TableCell>
              <TableCell>
                <b>Balance</b>
              </TableCell>
              <TableCell>
                <b>Description</b>
              </TableCell>
              <TableCell>
                <b>Action</b>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={12} align="center">
                  <Box display="flex" justifyContent="center" alignItems="center" py={2}>
                    <CircularProgress size={24} />
                    <Typography variant="body2" ml={1}>
                      Loading supplier ledgers...
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : supplierLedgers.length ? (
              supplierLedgers.map((ledger) => (
                <TableRow key={ledger.id}>
                  <TableCell>{ledger.Supplier.name}</TableCell>
                  <TableCell>{ledger.sub_supplier_name}</TableCell>
                  <TableCell>{ledger.Supplier.account_number}</TableCell>
                  <TableCell>{ledger.FinancialYear.description}</TableCell>
                  <TableCell>{ledger.Firm.name}</TableCell>
                  <TableCell>
                    {moment(ledger.bill_date).format("DD-MM-YYYY")}
                  </TableCell>
                  <TableCell>{ledger.bill_number}</TableCell>
                  <TableCell>{ledger.bill_type}</TableCell>
                  <TableCell>{ledger.credit_amount}</TableCell>
                  <TableCell>{ledger.balance}</TableCell>
                  <TableCell>{ledger.description}</TableCell>
                  <TableCell>
                    <Tooltip title="Edit Ledger" placement="left">
                      <IconButton
                        color="primary"
                        onClick={() => handleOpenEditDialog(ledger)}
                      >
                        <Edit />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="View History" placement="left">
                      <IconButton
                        color="secondary"
                        onClick={() => handleOpenHistoryDialog(ledger)}
                      >
                        <History />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete Ledger" placement="left">
                      <IconButton
                        color="error"
                        onClick={() => handleOpenDeleteDialog(ledger)}
                      >
                        <Delete />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={12} align="center">
                  <Typography variant="body2" color="text.secondary" py={2}>
                    No supplier ledgers found
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[1, 5, 10, 25, 50]}
          component="div"
          count={pagination.totalRecords || 0}
          rowsPerPage={pagination.pageSize || 10}
          page={pagination.currentPage - 1}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handlePageSizeChange}
        />
      </TableContainer>
      
      <BulkSupplierLedgerForm
        open={openBulkDialog}
        onClose={() => setOpenBulkDialog(false)}
        suppliers={suppliers}
        financialYears={financialYears}
        firms={firms}
        onSuccess={() => fetchSupplierLedgers(pagination.currentPage, pagination.pageSize)}
      />
    </Box>
  );
};

export default SuppliersLedger;
